import '@fpsm/cxy-tokens/tokens.css';

import { type FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cx } from '@linaria/core';

import { useExpand } from '@cx-modules/hooks';
import { useTranslation } from '@cx-modules/i18n-provider';
import { NoContent } from '@cx-modules/ui';

import { Chevron } from './components/Chevron';
import { BET_INCREMENT_NUMBER } from './components/MarketExpander/config';
import { OddsGridTable } from './components/OddsGridTable';
import { OddsGridBetRow } from './components/OddsGridTable/OddsGridBetRow';
import { OddsGridHeader } from './components/OddsGridTable/OddsGridHeader';
import { OddsGridContext } from './context/OddsGridContext';
import { countUniqueBookiesFromOdds } from './helpers/count-unique-bookies-from-odds';
import { useBets } from './hooks/useBets';
import { useBetsWithBestOdds } from './hooks/useBetsWithBestOdds';
import { useMobileViewport } from './hooks/useMobileViewport';
import { BUTTON_TEXT, useShowMore } from './hooks/useShowMore';
import { shallow, useSelector } from './providers/StoreProvider';
import { BetRow, MarketExpander, MarketHeader, OddsStreamController } from './components';
import {
  DesktopOnly,
  GridBackground,
  GridContentWrapper,
  MarketExpanderWrapper,
  MarketWrapper,
  MobileOnly,
  MobileOnlyGridWrapper,
  ShowMoreContainer,
  ShowMoreText,
  UnsupportedMessageContent,
  UnsupportedMessageWrapper,
} from './OddsGrid.style';
import { type OddsGridProps } from './types';

export const OddsGrid: FunctionComponent<OddsGridProps> = ({
  bestOddsStartExpanded,
  centrifugoUrl,
  enableLivePrices = false,
  enforceMobile,
  gridOnly = false,
  gridStartsExpanded,
  hideHistory,
  marketId,
  marketName,
  minUniqueBookies = 1,
  noDataAvailableFallbackText = 'No odds available at the moment. Check again later.',
  oddsShown,
  overrideBookies,
  showCompareOddsToolTip,
  showMoreBetsStartsExpanded,
  showSubeventData = false,
  subeventType,
}) => {
  const [bestOddsAreExpanded, toggleBestOddsAreExpanded] = useExpand(bestOddsStartExpanded);
  const [gridIsExpanded, setGridIsExpanded] = useState(gridOnly || gridStartsExpanded);
  const isMobileViewport = useMobileViewport();
  const isMobile = isMobileViewport || enforceMobile;
  const toggleGridExpanded = useCallback(() => setGridIsExpanded(prev => !prev), []);

  const {
    allowOddsClickout,
    disableStreamer,
    firstBetOnMobileStartsExpanded,
    oddsFormat,
    onOddsButtonClick,
    renderBadge,
    tooltipForNewUser,
  } = useSelector(
    store => ({
      allowOddsClickout: store.allowOddsClickout,
      disableStreamer: store.disableStreamer ?? false,
      firstBetOnMobileStartsExpanded: store.firstBetOnMobileStartsExpanded ?? false,
      oddsFormat: store.oddsFormat,
      onOddsButtonClick: store.onOddsButtonClick,
      renderBadge: store.renderOddsButtonBadge,
      tooltipForNewUser: store.tooltipForNewUser ?? false,
    }),
    shallow,
  );
  const marketRef = useRef<HTMLDivElement>(null);
  const toggleMarket = useCallback(() => {
    toggleBestOddsAreExpanded();
  }, [toggleBestOddsAreExpanded]);
  const { t } = useTranslation('label');

  useEffect(() => {
    // In gridOnly mode the accordion is not rendered; force the grid open and
    // skip accordion-driven collapse/expand logic so it stays permanently visible.
    if (gridOnly) {
      setGridIsExpanded(true);
      return;
    }

    // hide grid if top accordion closes
    if (!bestOddsAreExpanded) {
      setGridIsExpanded(false);
      return;
    }

    // Auto-expand on mobile (viewport or enforced)
    if (isMobile) {
      setGridIsExpanded(true);
    }

    // sync grid with accordion if grid starts expanded
    if (gridStartsExpanded) {
      setGridIsExpanded(true);
    }
  }, [bestOddsAreExpanded, gridOnly, gridStartsExpanded, isMobile]);

  const bets = useBets();
  const betsWithBestOdds = useBetsWithBestOdds();

  // Mobile "Show More" functionality
  const mobileContainerRef = useRef<HTMLDivElement>(null);

  const getInitialVisibleBetsCount = (totalBets: number, isExpanded: boolean): number => {
    const numberOfBetsForShowMoreBetsStartsExpanded = BET_INCREMENT_NUMBER * 2; // 16
    if (isExpanded) {
      return numberOfBetsForShowMoreBetsStartsExpanded;
    }
    // For collapsed state: show all if exactly 3, otherwise show 2
    return totalBets === 3 ? 3 : 2;
  };

  const initialVisibleBetsNumber = getInitialVisibleBetsCount(bets.length, showMoreBetsStartsExpanded);
  const showMoreBetsButtonVisible = bets.length > initialVisibleBetsNumber;

  const { setVisibleBetsNumber, showMoreButtonText, visibleBetsNumber } = useShowMore({
    allBetsNumber: bets.length,
    betIncrementNumber: 8,
    containerRef: mobileContainerRef,
    initialVisibleBetsNumber,
    scrollTopNumber: 150,
  });

  const visibleBets = useMemo(() => bets.slice(0, visibleBetsNumber), [bets, visibleBetsNumber]);

  if (countUniqueBookiesFromOdds(bets) < minUniqueBookies) {
    return <NoContent>{noDataAvailableFallbackText}</NoContent>;
  }

  const betsExist = bets.length > 0;
  const showGrid = betsExist && (gridOnly || gridIsExpanded);

  return (
    <MarketWrapper
      className={cx(!allowOddsClickout && 'not-supported')}
      data-testid="odds-grid"
      ref={marketRef}
      {...(enforceMobile ? { 'data-enforce-mobile': 'mobile' } : {})}
    >
      <OddsStreamController enabled={enableLivePrices && Boolean(showGrid)} marketId={marketId} url={centrifugoUrl} />
      {!gridOnly && (
        <MarketExpanderWrapper id={`market_${marketId}`}>
          <MarketExpander
            gridIsExpanded={!!gridIsExpanded}
            marketId={marketId}
            marketName={marketName}
            showCompareOddsToolTip={!!showCompareOddsToolTip}
            showMoreBetsStartsExpanded={showMoreBetsStartsExpanded}
            showSubeventData={showSubeventData}
            startsExpanded={bestOddsAreExpanded}
            subeventType={subeventType}
            toggleGrid={toggleGridExpanded}
            toggleMarket={toggleMarket}
          />
        </MarketExpanderWrapper>
      )}
      {showGrid && (
        <GridBackground>
          <GridContentWrapper>
            <OddsGridContext.Provider value={{ isTabletOrDesktop: true, oddsShown, overrideBookies }}>
              <DesktopOnly data-testid="odds-grid-desktop">
                {!gridOnly && <MarketHeader marketName={marketName} />}
                <div id="offer-portal" />
                <OddsGridTable header={<OddsGridHeader />}>
                  {(sportsbookIds, exchangeIds) =>
                    bets.map(bet => (
                      <OddsGridBetRow
                        bet={bet}
                        betsWithBestOdds={betsWithBestOdds}
                        exchangeIds={exchangeIds}
                        key={bet.id}
                        marketId={marketId}
                        marketName={marketName}
                        oddsFormat={oddsFormat}
                        showHistory={!hideHistory}
                        sportsbookIds={sportsbookIds}
                      />
                    ))
                  }
                </OddsGridTable>
              </DesktopOnly>
            </OddsGridContext.Provider>

            <OddsGridContext.Provider value={{ isTabletOrDesktop: false, oddsShown, overrideBookies }}>
              <MobileOnly data-testid="odds-grid-mobile">
                {!allowOddsClickout && (
                  <UnsupportedMessageWrapper>
                    <UnsupportedMessageContent>
                      {t('CX_ODDS_GRID_UNSUPPORTED_STATE', { ns: 'label' })}
                    </UnsupportedMessageContent>
                  </UnsupportedMessageWrapper>
                )}
                <MobileOnlyGridWrapper ref={mobileContainerRef}>
                  {visibleBets.map((bet, index) => (
                    <BetRow
                      bet={bet}
                      betsWithBestOdds={betsWithBestOdds}
                      defaultExpanded={index === 0 && firstBetOnMobileStartsExpanded}
                      disableStreamer={disableStreamer}
                      index={index}
                      isTabletOrDesktop={false}
                      key={bet.id}
                      marketId={marketId}
                      oddsFormat={oddsFormat}
                      onOddsButtonClick={onOddsButtonClick}
                      renderBadge={renderBadge}
                      showCompareOddsToolTip={!!showCompareOddsToolTip && index === 0}
                      tooltipForNewUser={tooltipForNewUser}
                    />
                  ))}
                  {showMoreBetsButtonVisible && (
                    <ShowMoreContainer aria-expanded={showMoreButtonText === BUTTON_TEXT.SHOW_LESS}>
                      <ShowMoreText onClick={setVisibleBetsNumber}>
                        {t(showMoreButtonText, { ns: 'label' })}
                      </ShowMoreText>
                      <Chevron expanded={showMoreButtonText === BUTTON_TEXT.SHOW_LESS} strokeWidth={3.5} width={14} />
                    </ShowMoreContainer>
                  )}
                </MobileOnlyGridWrapper>
              </MobileOnly>
            </OddsGridContext.Provider>
          </GridContentWrapper>
        </GridBackground>
      )}
    </MarketWrapper>
  );
};
